import { isStudioThemeId, type StudioThemeId } from '../music24/studioThemes';

/**
 * The SmartLink Media block: one picture or one video on a page (design
 * docs/superpowers/design-reference/music24-smartlink-media-block, owner answers 2026-10-08).
 *
 * The block keeps a SOURCE, never a file of a Studio asset. The server resolves the newest ready
 * revision of that source on every read, so a remake in the Studio shows on the page with no edit.
 * An upload is the one source that names a file.
 *
 * This file has the stored shape (`RawMediaWidgetData`), the answer shape
 * (`SerializedMediaWidgetData`), the one validation rule (`parseMediaWidgetData`) and the event
 * rules of the public page. music24-api, music24-app and sml-preview read it.
 */

/*
 * The stored shapes are type aliases, not interfaces: a type alias of plain JSON values fits the
 * JSON types of Prisma (`InputJsonValue`, `JsonValue`) with no cast. An interface does not, because
 * it has no index signature.
 */

/** A release kit asset of a song: its asset type (`themeId`) and its output (`variant`). */
export type MediaWidgetKitAssetSource = {
  kind: 'kitAsset';
  catalogItemUuid: string;
  themeId: StudioThemeId;
  variant: string;
};

/** An AI reel of a song: the newest ready reel of that length (`15s` or `30s`). */
export type MediaWidgetReelSource = {
  kind: 'reel';
  catalogItemUuid: string;
  reelVariant: MediaWidgetReelVariant;
};

/** A file the artist uploaded: an image (`POST /accounts/:id/files`) or a video (the post media upload). */
export type MediaWidgetUploadSource = {
  kind: 'upload';
  fileId: number;
  /**
   * True only on a block that the Image block fold migrated (Prisma migration
   * `smartlink_media_block_image_blocks`). Such a block may keep a shared file with no account (an
   * old Image block upload). The server never takes this flag from a request: it keeps it only
   * while the block keeps the same file (`withTrustedLegacyImageFlag` in music24-api).
   */
  legacyImage?: true;
};

/**
 * The cover of a song (owner request 2026-10-09): the artwork of the catalog row now. The cover
 * editor, an upload and the stored copy of a claimed cover all write the same row
 * (`AccountCatalogItemModel.artworkFileId`, else `artworkUrl`), so the block follows a new cover
 * with no edit.
 */
export type MediaWidgetCoverSource = {
  kind: 'cover';
  catalogItemUuid: string;
};

export type MediaWidgetSource =
  | MediaWidgetKitAssetSource
  | MediaWidgetReelSource
  | MediaWidgetCoverSource
  | MediaWidgetUploadSource;

export const MEDIA_WIDGET_REEL_VARIANTS = ['15s', '30s'] as const;
export type MediaWidgetReelVariant = (typeof MEDIA_WIDGET_REEL_VARIANTS)[number];

/** The frame of the block. `original` keeps the ratio of the file. */
export const MEDIA_WIDGET_SHAPES = ['original', 'square', 'portrait', 'wide'] as const;
export type MediaWidgetShape = (typeof MEDIA_WIDGET_SHAPES)[number];

/** How a video sits in its frame: `cover` fills it, `contain` shows all of it. An image always fills. */
export const MEDIA_WIDGET_FITS = ['cover', 'contain'] as const;
export type MediaWidgetFit = (typeof MEDIA_WIDGET_FITS)[number];

/** The CSS aspect ratio of each fixed shape (width / height). */
export const MEDIA_WIDGET_SHAPE_RATIOS: Readonly<Record<Exclude<MediaWidgetShape, 'original'>, number>> = {
  square: 1,
  portrait: 4 / 5,
  wide: 16 / 9,
};

export const MEDIA_WIDGET_CAPTION_MAX_LENGTH = 200;
export const MEDIA_WIDGET_LINK_MAX_LENGTH = 2048;

export type MediaWidgetOptions = {
  /** A video starts muted when 50 % of it is on the screen (owner answer 2). Default true. */
  autoplay: boolean;
  /** Default true. */
  loop: boolean;
  /** Show the play, sound and time controls. Default false. */
  controls: boolean;
  shape: MediaWidgetShape;
  fit: MediaWidgetFit;
  caption: string | null;
  /** "Tap opens": a tap on the block opens this address and counts as a click of the block. */
  linkUrl: string | null;
};

export const DEFAULT_MEDIA_WIDGET_OPTIONS: Readonly<MediaWidgetOptions> = {
  autoplay: true,
  loop: true,
  controls: false,
  shape: 'original',
  fit: 'cover',
  caption: null,
  linkUrl: null,
};

/** The stored `SmartLinkWidgetModel.data` of a media block. */
export type RawMediaWidgetData = MediaWidgetOptions & {
  source: MediaWidgetSource;
};

export type MediaWidgetFileKind = 'image' | 'video';

/** The file that a media block shows now. Every url is absolute and public. */
export interface ResolvedMediaFile {
  kind: MediaWidgetFileKind;
  /**
   * An image: the resized image (imgproxy, 2000 px wide). A video: the file route with
   * `?disposition=inline`, which answers Range requests.
   */
  url: string;
  /** An image only: the resized copies for `srcset`, smallest first. Empty for a video. */
  srcSet: { width: number; url: string }[];
  /** The poster of a video; null for an image or a video with no poster. */
  posterUrl: string | null;
  /** A small picture for the editor list (a thumbnail, else the poster, else the image). */
  thumbUrl: string | null;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  /** The revision of a Studio asset or a reel (r1, r2); null for an upload and a cover. */
  revision: number | null;
  /** The file name of an upload; null for a Studio source. */
  fileName: string | null;
  /**
   * The public YouTube video of this asset (one-off publish, `oneOffPublish.ts`). When it is set,
   * the page plays the YouTube embed (the views count on YouTube) and keeps `url` as the fallback.
   * Null (or absent from an older API) while the video is not public, removed or private.
   */
  youtube?: ResolvedMediaYouTube | null;
}

/** A public YouTube video that a Media block plays in place of our file. */
export interface ResolvedMediaYouTube {
  videoId: string;
  /** `youtubeEmbedUrl(videoId)`. */
  embedUrl: string;
  watchUrl: string;
}

/**
 * Why a media block shows nothing (or an older file). The editor shows a warning (design frame d).
 * - `fileGone`: the asset, the reel or the upload is deleted, no revision has a file, or the song has no cover.
 * - `songGone`: the song left the Catalog.
 * - `rendering`: nothing is ready yet, but a revision is being made.
 */
export const MEDIA_WIDGET_PROBLEMS = ['fileGone', 'songGone', 'rendering'] as const;
export type MediaWidgetProblem = (typeof MEDIA_WIDGET_PROBLEMS)[number];

/** A newer revision that is being made while the block shows an older ready one (design d3). */
export interface MediaWidgetRenderingRevision {
  revision: number | null;
  /** 0..100 */
  progress: number;
}

/**
 * The answer shape of a media block, to the editor and to the public page. `media` is null when
 * nothing is ready: the public page then shows nothing, the editor preview draws an outline.
 */
export interface SerializedMediaWidgetData extends MediaWidgetOptions {
  source: MediaWidgetSource;
  media: ResolvedMediaFile | null;
  problem: MediaWidgetProblem | null;
  renderingRevision: MediaWidgetRenderingRevision | null;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const VARIANT_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function includes<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === 'string' && (values as readonly string[]).includes(value);
}

/** The source of a media block, or null when the value is not a valid source. */
export function parseMediaWidgetSource(value: unknown): MediaWidgetSource | null {
  if (!isRecord(value)) {
    return null;
  }
  switch (value.kind) {
    case 'kitAsset':
      if (
        typeof value.catalogItemUuid !== 'string' ||
        !UUID_PATTERN.test(value.catalogItemUuid) ||
        !isStudioThemeId(value.themeId) ||
        typeof value.variant !== 'string' ||
        !VARIANT_PATTERN.test(value.variant)
      ) {
        return null;
      }
      return {
        kind: 'kitAsset',
        catalogItemUuid: value.catalogItemUuid.toLowerCase(),
        themeId: value.themeId,
        variant: value.variant,
      };
    case 'reel':
      if (
        typeof value.catalogItemUuid !== 'string' ||
        !UUID_PATTERN.test(value.catalogItemUuid) ||
        !includes(MEDIA_WIDGET_REEL_VARIANTS, value.reelVariant)
      ) {
        return null;
      }
      return { kind: 'reel', catalogItemUuid: value.catalogItemUuid.toLowerCase(), reelVariant: value.reelVariant };
    case 'cover':
      if (typeof value.catalogItemUuid !== 'string' || !UUID_PATTERN.test(value.catalogItemUuid)) {
        return null;
      }
      return { kind: 'cover', catalogItemUuid: value.catalogItemUuid.toLowerCase() };
    case 'upload':
      if (typeof value.fileId !== 'number' || !Number.isSafeInteger(value.fileId) || value.fileId <= 0) {
        return null;
      }
      return value.legacyImage === true
        ? { kind: 'upload', fileId: value.fileId, legacyImage: true }
        : { kind: 'upload', fileId: value.fileId };
    default:
      return null;
  }
}

/** True for an http or https address that a fan can open. */
export function isMediaWidgetLinkUrl(value: string): boolean {
  if (value.length > MEDIA_WIDGET_LINK_MAX_LENGTH) {
    return false;
  }
  return /^https?:\/\/[^\s/?#]+\.[^\s/?#]+([/?#][^\s]*)?$/i.test(value);
}

function optionalText(value: unknown, maxLength: number): string | null | undefined {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length > maxLength) {
    return undefined;
  }
  return trimmed === '' ? null : trimmed;
}

/**
 * The stored data of a media block from a request body, with the defaults for every missing
 * option. Null when a field is invalid (the API answers 400). The one validation rule: the server
 * and the editor both call it.
 */
export function parseMediaWidgetData(value: unknown): RawMediaWidgetData | null {
  if (!isRecord(value)) {
    return null;
  }
  const source = parseMediaWidgetSource(value.source);
  if (source === null) {
    return null;
  }
  const flag = (key: 'autoplay' | 'loop' | 'controls'): boolean | null => {
    const flagValue = value[key];
    if (flagValue === undefined) {
      return DEFAULT_MEDIA_WIDGET_OPTIONS[key];
    }
    return typeof flagValue === 'boolean' ? flagValue : null;
  };
  const autoplay = flag('autoplay');
  const loop = flag('loop');
  const controls = flag('controls');
  if (autoplay === null || loop === null || controls === null) {
    return null;
  }
  const shape = value.shape === undefined ? DEFAULT_MEDIA_WIDGET_OPTIONS.shape : value.shape;
  const fit = value.fit === undefined ? DEFAULT_MEDIA_WIDGET_OPTIONS.fit : value.fit;
  if (!includes(MEDIA_WIDGET_SHAPES, shape) || !includes(MEDIA_WIDGET_FITS, fit)) {
    return null;
  }
  const caption = optionalText(value.caption, MEDIA_WIDGET_CAPTION_MAX_LENGTH);
  const linkUrl = optionalText(value.linkUrl, MEDIA_WIDGET_LINK_MAX_LENGTH);
  if (caption === undefined || linkUrl === undefined) {
    return null;
  }
  if (linkUrl !== null && !isMediaWidgetLinkUrl(linkUrl)) {
    return null;
  }
  return { source, autoplay, loop, controls, shape, fit, caption, linkUrl };
}

/**
 * The stored data of a new Media block with an uploaded image: an upload source with the image.
 * The SQL of the Prisma migration `smartlink_media_block_image_blocks` writes the same shape for
 * an old Image block (`{ imageId }`), plus `source.legacyImage: true`.
 */
export function mediaWidgetDataOfImageBlock(imageId: number): RawMediaWidgetData {
  return { ...DEFAULT_MEDIA_WIDGET_OPTIONS, source: { kind: 'upload', fileId: imageId } };
}

/** Two sources name the same thing (the editor marks the picked tile). */
export function isSameMediaWidgetSource(a: MediaWidgetSource, b: MediaWidgetSource): boolean {
  switch (a.kind) {
    case 'kitAsset':
      return (
        b.kind === 'kitAsset' &&
        a.catalogItemUuid === b.catalogItemUuid &&
        a.themeId === b.themeId &&
        a.variant === b.variant
      );
    case 'reel':
      return b.kind === 'reel' && a.catalogItemUuid === b.catalogItemUuid && a.reelVariant === b.reelVariant;
    case 'cover':
      return b.kind === 'cover' && a.catalogItemUuid === b.catalogItemUuid;
    case 'upload':
      return b.kind === 'upload' && a.fileId === b.fileId;
  }
}

/* ------------------------------------------------------------------------------------------------
 * Public page events (owner answer 2). One rule for the page, the endpoint and the stats.
 * ---------------------------------------------------------------------------------------------- */

export const SMARTLINK_MEDIA_EVENT_TYPES = ['media_view', 'media_play', 'media_unmute', 'media_progress'] as const;
export type SmartLinkMediaEventType = (typeof SMARTLINK_MEDIA_EVENT_TYPES)[number];

/** `media_view`: this share of the block is on the screen ... */
export const MEDIA_VIEW_VISIBLE_RATIO = 0.5;
/** ... for this long, once per page view. */
export const MEDIA_VIEW_VISIBLE_MS = 1000;
/** A muted autoplay counts as a play after this much watching. A tap counts at once. */
export const MEDIA_AUTOPLAY_PLAY_AFTER_MS = 3000;
/** `media_progress` goes once for each of these points of a play (percent of the length). */
export const MEDIA_PROGRESS_QUARTILES = [25, 50, 75, 100] as const;
export type MediaProgressQuartile = (typeof MEDIA_PROGRESS_QUARTILES)[number];

export function isMediaProgressQuartile(value: unknown): value is MediaProgressQuartile {
  return typeof value === 'number' && (MEDIA_PROGRESS_QUARTILES as readonly number[]).includes(value);
}
